// Section 21 (stream G): a small inline-SVG line chart for the Ledger's series (inequality,
// literacy, fragility). One series, so no legend: the caption names it. 2px line in the accent,
// hairline recessive gridlines, year ticks on the x axis, optional event markers (8px dots with
// a surface ring) along the baseline, a crosshair with a tooltip that snaps to the nearest
// season (pointer and arrow keys), and a table view for screen readers and print. Props only.
import { useState } from 'preact/hooks';
import { ledgerChartText } from '../text/en';

export interface LinePoint {
  x: number;
  y: number;
}

export interface LineMark {
  x: number;
  label: string;
}

interface Props {
  id: string;
  title: string;
  points: readonly LinePoint[];
  /** Y range; the max defaults to the data max (rounded up). */
  yMin?: number;
  yMax?: number;
  formatY: (y: number) => string;
  /** A season's label, e.g. "Autumn, year 3". */
  formatX: (x: number) => string;
  marks?: readonly LineMark[];
  seasonsPerYear: number;
}

const W = 640;
const H = 200;
const M = { top: 12, right: 16, bottom: 30, left: 44 };

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 2.5, 5, 10]) if (max <= step * pow) return step * pow;
  return 10 * pow;
}

export function LineChart({
  id,
  title,
  points,
  yMin = 0,
  yMax,
  formatY,
  formatX,
  marks = [],
  seasonsPerYear,
}: Props) {
  const [hover, setHover] = useState<number | null>(null);
  if (points.length === 0) {
    return (
      <figure class="ledger-chart" aria-labelledby={`${id}-title`}>
        <figcaption id={`${id}-title`}>{title}</figcaption>
        <p class="hint">{ledgerChartText.noData}</p>
      </figure>
    );
  }
  const ys = points.map((p) => p.y);
  const top = yMax ?? niceMax(Math.max(...ys));
  const xFirst = points[0]?.x ?? 1;
  const xLast = points[points.length - 1]?.x ?? xFirst;
  const span = Math.max(1, xLast - xFirst);
  const iw = W - M.left - M.right;
  const ih = H - M.top - M.bottom;
  // A single season sits in the middle of the plot, not on the y-axis labels (M7).
  const sx = (x: number): number =>
    points.length === 1 ? M.left + iw / 2 : M.left + ((x - xFirst) / span) * iw;
  const sy = (y: number): number =>
    M.top + ih - ((Math.min(top, Math.max(yMin, y)) - yMin) / (top - yMin || 1)) * ih;
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`)
    .join(' ');
  const yTicks = [yMin, (yMin + top) / 2, top];
  const years = Math.ceil(xLast / seasonsPerYear);
  const every = Math.max(1, Math.ceil(years / 10));
  const xTicks: number[] = [];
  for (let y = 1; y <= years; y += every) {
    const x = (y - 1) * seasonsPerYear + 1;
    if (x >= xFirst && x <= xLast) xTicks.push(x);
  }
  const nearest = (x: number): number => {
    let best = 0;
    points.forEach((p, i) => {
      if (Math.abs(p.x - x) < Math.abs((points[best]?.x ?? 0) - x)) best = i;
    });
    return best;
  };
  const hovered = hover === null ? undefined : points[hover];
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const last = ys[ys.length - 1] ?? 0;
  const markAt = (x: number) => marks.filter((m) => m.x === x);
  return (
    <figure class="ledger-chart" aria-labelledby={`${id}-title`}>
      <figcaption id={`${id}-title`}>{title}</figcaption>
      <svg
        class="ledger-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={ledgerChartText.summary(
          title,
          points.length,
          formatY(min),
          formatY(max),
          formatY(last),
        )}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          const i = hover ?? (e.key === 'ArrowLeft' ? points.length : -1);
          setHover(Math.max(0, Math.min(points.length - 1, i + (e.key === 'ArrowLeft' ? -1 : 1))));
        }}
        onBlur={() => setHover(null)}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line class="ledger-chart__grid" x1={M.left} x2={W - M.right} y1={sy(t)} y2={sy(t)} />
            <text class="ledger-chart__tick" x={M.left - 6} y={sy(t) + 4} text-anchor="end">
              {formatY(t)}
            </text>
          </g>
        ))}
        {xTicks.map((x) => (
          <text
            key={x}
            class="ledger-chart__tick"
            x={sx(x)}
            y={H - M.bottom + 16}
            text-anchor="middle"
          >
            {ledgerChartText.year(Math.floor((x - 1) / seasonsPerYear) + 1)}
          </text>
        ))}
        <path class="ledger-chart__line" d={path} />
        {marks.map((m) => (
          <circle key={`m${m.x}`} class="ledger-chart__mark" cx={sx(m.x)} cy={M.top + ih} r={4}>
            <title>{m.label}</title>
          </circle>
        ))}
        {hovered !== undefined && (
          <g class="ledger-chart__hover" aria-hidden="true">
            <line x1={sx(hovered.x)} x2={sx(hovered.x)} y1={M.top} y2={M.top + ih} />
            <circle cx={sx(hovered.x)} cy={sy(hovered.y)} r={4} />
            <text
              x={Math.min(W - M.right - 4, Math.max(M.left + 4, sx(hovered.x)))}
              y={M.top + 10}
              text-anchor={sx(hovered.x) > W / 2 ? 'end' : 'start'}
            >
              {ledgerChartText.point(formatX(hovered.x), formatY(hovered.y))}
              {markAt(hovered.x).length > 0
                ? ` · ${markAt(hovered.x)
                    .map((m) => m.label)
                    .join('; ')}`
                : ''}
            </text>
          </g>
        )}
        <rect
          class="ledger-chart__hit"
          x={M.left}
          y={M.top}
          width={iw}
          height={ih}
          onMouseMove={(e) => {
            const svg = e.currentTarget.ownerSVGElement;
            if (svg === null) return;
            const box = svg.getBoundingClientRect();
            const vx = ((e.clientX - box.left) / Math.max(1, box.width)) * W;
            setHover(nearest(xFirst + ((vx - M.left) / iw) * span));
          }}
          onMouseLeave={() => setHover(null)}
        />
      </svg>
      <details class="ledger-chart__table">
        <summary>{ledgerChartText.showTable}</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">{ledgerChartText.season}</th>
              <th scope="col">{ledgerChartText.value}</th>
              {marks.length > 0 && <th scope="col">{ledgerChartText.events}</th>}
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.x}>
                <td>{formatX(p.x)}</td>
                <td class="num">{formatY(p.y)}</td>
                {marks.length > 0 && (
                  <td>
                    {markAt(p.x)
                      .map((m) => m.label)
                      .join('; ')}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
